/**
 * Масштаб і поворот зображення в переглядачі.
 *
 * Масштаб — або «вписати» (зображення цілком у видиму область, не більше
 * 100 %), або явне число. Поворот — кратний 90°, тож для 90° і 270°
 * вписування рахується з переставленими шириною й висотою.
 */

export type DiskImageZoom = 'fit' | number

export const IMAGE_ZOOM_MIN = 0.05
export const IMAGE_ZOOM_MAX = 8
/** Крок кнопок «+»/«−» і одного «клацання» колеса. */
export const IMAGE_ZOOM_STEP = 1.25

export interface Size {
    width: number
    height: number
}

export const clampZoom = (scale: number): number => Math.min(IMAGE_ZOOM_MAX, Math.max(IMAGE_ZOOM_MIN, scale))

/** Поворот за годинниковою стрілкою (`+90`) чи проти (`-90`), у межах 0…270. */
export const rotate = (rotation: number, delta: 90 | -90): number => (((rotation + delta) % 360) + 360) % 360

/** Розміри зображення після повороту. */
export const rotatedSize = (natural: Size, rotation: number): Size =>
    rotation % 180 === 0 ? natural : { width: natural.height, height: natural.width }

/** Масштаб «вписати»: усе зображення видно, дрібне не розтягуємо понад 100 %. */
export const fitScale = (natural: Size, box: Size, rotation: number): number => {
    const size = rotatedSize(natural, rotation)

    if (size.width <= 0 || size.height <= 0 || box.width <= 0 || box.height <= 0) return 1

    return clampZoom(Math.min(1, box.width / size.width, box.height / size.height))
}

/** Фактичний масштаб: для «вписати» — розрахований. */
export const effectiveScale = (zoom: DiskImageZoom, natural: Size, box: Size, rotation: number): number =>
    zoom === 'fit' ? fitScale(natural, box, rotation) : zoom

/** Наступний масштаб після «+» (`direction = 1`) чи «−» (`-1`), від поточного фактичного. */
export const stepZoom = (current: number, direction: 1 | -1): number =>
    clampZoom(direction > 0 ? current * IMAGE_ZOOM_STEP : current / IMAGE_ZOOM_STEP)

/** Підпис масштабу: «100 %», «37 %». */
export const zoomLabel = (scale: number): string => `${Math.round(scale * 100)} %`
